import {
  AreaSeries,
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  LineType,
  createChart,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type MouseEventParams,
  type Time,
} from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";
import { money } from "../format";
import type { ChartPoint } from "../types";

export type ChartMode = "price" | "performance";

type Props = {
  aster: ChartPoint[];
  crypto: ChartPoint[];
  cryptoName: string;
  currency: "BRL" | "USD";
  usdBrl: number;
  mode: ChartMode;
  showGrid: boolean;
  resetNonce: number;
};

type HoverState = {
  time: string;
  aster?: number;
  crypto?: number;
};

function extendToToday(points: ChartPoint[]) {
  if (!points.length) return [];
  const today = new Date().toISOString().slice(0, 10);
  const last = points.at(-1)!;
  return last.time === today ? points : [...points, { ...last, time: today, note: "Preço vigente" }];
}

function performance(points: ChartPoint[]) {
  const base = points[0]?.value;
  if (!base) return [];
  return points.map((point) => ({ time: point.time as Time, value: (point.value / base - 1) * 100 }));
}

export function AsterTerminalChart({ aster, crypto, cryptoName, currency, usdBrl, mode, showGrid, resetNonce }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  const prepared = useMemo(() => {
    const extended = extendToToday(aster);
    if (!extended.length) return { aster: [], crypto: [] };
    if (mode === "performance") {
      return { aster: performance(extended), crypto: performance(crypto) };
    }
    const divisor = currency === "USD" ? usdBrl || 1 : 1;
    return {
      aster: extended.map((point) => ({ time: point.time as Time, value: point.value / divisor })),
      crypto: [],
    };
  }, [aster, crypto, currency, mode, usdBrl]);

  useEffect(() => {
    if (!containerRef.current || !prepared.aster.length) return;
    const formatter = (value: number) => mode === "price" ? money(value, currency, 4) : `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
    const gridColor = showGrid ? "rgba(255,255,255,0.055)" : "transparent";
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#7f8aa8",
        fontFamily: "Geist Mono Variable, ui-monospace, monospace",
        attributionLogo: true,
      },
      localization: { locale: "pt-BR", priceFormatter: formatter },
      grid: { vertLines: { color: gridColor }, horzLines: { color: gridColor } },
      rightPriceScale: {
        borderColor: "rgba(255,255,255,0.08)",
        scaleMargins: { top: 0.12, bottom: 0.12 },
      },
      timeScale: {
        borderColor: "rgba(255,255,255,0.08)",
        timeVisible: false,
        rightOffset: 4,
        barSpacing: 28,
        minBarSpacing: 7,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "rgba(145,132,255,0.6)", style: LineStyle.Dashed, labelBackgroundColor: "#6557d8" },
        horzLine: { color: "rgba(145,132,255,0.5)", style: LineStyle.Dashed, labelBackgroundColor: "#6557d8" },
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
    });
    chartRef.current = chart;

    const asterSeries = mode === "price"
      ? chart.addSeries(AreaSeries, {
          lineColor: "#8a78ff",
          topColor: "rgba(123,108,255,0.38)",
          bottomColor: "rgba(123,108,255,0.015)",
          lineWidth: 3,
          lineType: LineType.WithSteps,
          priceLineVisible: true,
          priceLineColor: "rgba(138,120,255,0.55)",
          lastValueVisible: true,
          title: "ASTER",
          priceFormat: { type: "custom", formatter, minMove: 0.0001 },
        })
      : chart.addSeries(LineSeries, {
          color: "#8a78ff",
          lineWidth: 3,
          lineType: LineType.WithSteps,
          priceLineVisible: false,
          lastValueVisible: true,
          title: "ASTER",
          priceFormat: { type: "custom", formatter, minMove: 0.01 },
        });
    asterSeries.setData(prepared.aster);

    const visibleMarkers = aster.slice(-8);
    const originalMarkers = visibleMarkers.map((point, index) => ({
      time: point.time as Time,
      position: "aboveBar" as const,
      color: index === visibleMarkers.length - 1 ? "#45d798" : "#a493ff",
      shape: "circle" as const,
      text: index === 0 ? "Início ASTER" : index === visibleMarkers.length - 1 ? "Fechamento atual" : "",
    }));
    const markers = createSeriesMarkers(asterSeries, originalMarkers);

    let cryptoSeries: ISeriesApi<"Line"> | null = null;
    if (mode === "performance" && prepared.crypto.length) {
      cryptoSeries = chart.addSeries(LineSeries, {
        color: "#41b8ff",
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: true,
        title: cryptoName,
        priceFormat: { type: "custom", formatter, minMove: 0.01 },
      });
      cryptoSeries.setData(prepared.crypto);
    }

    const crosshair = (param: MouseEventParams<Time>) => {
      if (!param.time) return setHover(null);
      const asterValue = param.seriesData.get(asterSeries) as { value?: number } | undefined;
      const cryptoValue = cryptoSeries ? param.seriesData.get(cryptoSeries) as { value?: number } | undefined : undefined;
      setHover({ time: String(param.time), aster: asterValue?.value, crypto: cryptoValue?.value });
    };
    chart.subscribeCrosshairMove(crosshair);
    chart.timeScale().fitContent();

    return () => {
      markers.detach();
      chart.unsubscribeCrosshairMove(crosshair);
      chart.remove();
      chartRef.current = null;
    };
  }, [aster, cryptoName, currency, mode, prepared, showGrid, resetNonce]);

  return (
    <div className="chart-stage">
      <div ref={containerRef} className="terminal-chart" aria-label="Gráfico interativo do ASTER" />
      <div className="chart-floating-legend">
        <span><i className="legend-aster" /> ASTER</span>
        {mode === "performance" && prepared.crypto.length > 0 && <span><i className="legend-crypto" /> {cryptoName}</span>}
      </div>
      {mode === "performance" && aster.length > 0 && crypto.length > 0 && crypto[0].time < aster[0].time && (
        <div className="chart-history-note">
          <strong>ASTER entra em {new Date(`${aster[0].time}T12:00:00`).toLocaleDateString("pt-BR")}</strong>
          <span>A cripto mantém todo o período selecionado. Cada série usa seu próprio primeiro ponto como base 0%.</span>
        </div>
      )}
      {hover && (
        <div className="chart-tooltip">
          <time>{new Date(`${hover.time}T12:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}</time>
          {hover.aster !== undefined && <strong>ASTER <b>{mode === "price" ? money(hover.aster, currency, 4) : `${hover.aster >= 0 ? "+" : ""}${hover.aster.toFixed(2)}%`}</b></strong>}
          {hover.crypto !== undefined && <strong>{cryptoName} <b>{hover.crypto >= 0 ? "+" : ""}{hover.crypto.toFixed(2)}%</b></strong>}
        </div>
      )}
    </div>
  );
}
